import React, { useEffect } from 'react';
import {
    FileSignature, ShieldCheck, CreditCard, Video, LayoutGrid,
    Clock, CheckCircle2, Zap, MoveRight, Download, ExternalLink,
} from 'lucide-react';

/*
 * Nidhi Katiyar onboarding hub — 6-month content system (60 videos/mo).
 * Built from .claude/skills/onboard-client/onboarding-template.jsx.
 * Scope: strategy + recording support + clipping + first-pass editing + publishing (no full
 * final edit at this stage), no AI clone. Agreement + invoice are active steps (not yet
 * signed/paid). Publishing added 2026-08-04 per her WhatsApp confirmation; SLA is now v2.
 */

// --- Per-client fill-ins ---
const SLUG = 'nidhi';
const SHARE_EMAIL = 'wasnot005@gmail.com';
const NOTION_CONTENT_OS = 'https://www.notion.so/Media-Tracker-3443ac2a530981f7bdb8d2beddfc089a';

const data = {
    client: 'Nidhi',
    brandLabel: 'Nidhi Katiyar',
    pageTitle: 'Onboarding | Nidhi Katiyar via SocialSEO',
    badge: 'Ready to Start',
    headline: 'Welcome aboard, Nidhi.',

    greeting: 'Hi Nidhi,',
    intro: [
        'Great to have you in. This page is your single home base for getting started. Everything you need in the first few days is a short checklist below.',
        'A quick note on how it runs. This is a 6-month content system, 60 videos a month, where we handle the strategy, help you record, clip your long-form, deliver a first-pass edit, and post everything for you. You add your captions and colour on top of our cut, send it back, and we schedule and publish it. At or before month 3 we review and expand the scope together and revisit the agreement.',
        'Work down the steps. Once the agreement is signed and the first invoice is cleared, we move straight into strategy and your first batch.',
    ],

    steps: [
        {
            icon: FileSignature,
            title: 'Review & sign your agreement',
            desc: 'Please read it so we are fully aligned on the scope: 60 videos a month with strategy, recording support, clipping, first-pass editing, and publishing, over a 6-month term. We need it signed before production begins.',
            links: [
                { label: 'Service Agreement (SLA)', href: `/assets/${SLUG}/agreement.pdf`, icon: Download },
            ],
        },
        {
            icon: CreditCard,
            title: 'Clear your first invoice',
            desc: 'The first month is ₹80,000 + 18% GST (CGST 9% + SGST 9%), a total of ₹94,400, payable by bank transfer. Full account details are printed on the invoice. This first payment kicks off strategy and your first production cycle.',
            links: [
                { label: 'Download Invoice (bank details inside)', href: `/assets/${SLUG}/invoice.pdf`, icon: Download },
            ],
        },
        {
            icon: Video,
            title: 'Share your library, recording rhythm, and posting access',
            desc: `Three things from your side. Share access to your existing long-form, lives, and prior recordings so we can clip and repurpose them, pick a recurring recording slot for the new videos, and give us posting access so we can schedule and publish for you. Send your library to ${SHARE_EMAIL}.`,
            links: [],
            tips: [
                'Existing content: long-form videos, lives, podcast appearances, and any prior recordings we can clip.',
                'Recording slot: a recurring window where you record the new videos from our prompts and direction.',
                'Posting access: either direct account access or an invite to our scheduling tool, so publishing never waits on you.',
                `Share everything to ${SHARE_EMAIL}.`,
            ],
        },
        {
            icon: LayoutGrid,
            title: 'Open your Content OS',
            desc: 'Your Notion workspace is where the whole engine lives: the content plan, the scripts and clips pipeline, the posting calendar, and your approvals. This is where you give inputs and approve.',
            links: [
                { label: 'Open your Content OS', href: NOTION_CONTENT_OS, icon: ExternalLink },
            ],
        },
    ],

    timelineTitle: 'What the engagement looks like',
    phases: [
        {
            title: 'Onboarding & Setup',
            window: 'Days 1–7',
            items: [
                'Agreement signed and first invoice cleared.',
                'We deploy your Notion Content OS and lock your strategy and monthly content plan.',
                'We mine your existing library for clips and prep your first batch.',
                'Posting access is set up and your content calendar is live.',
            ],
        },
        {
            title: 'First Batch',
            window: 'Weeks 1–2',
            items: [
                'Your first recording session, from our prompts and direction.',
                'Clips from your existing library start flowing.',
                'First-pass edits delivered for your review. You add captions and colour, send back, we publish.',
            ],
        },
        {
            title: 'Full Production',
            window: 'Month 1',
            items: [
                '60 videos ship across the month: strategy, recording support, clipping, first-pass editing, and publishing.',
                'A steady weekly rhythm so output never depends on finding free time.',
            ],
        },
        {
            title: 'Scope Review',
            window: 'By Month 3',
            items: [
                'We review performance and expand the scope together, for example into complete end-to-end editing.',
                'The agreement is revisited and the fee adjusted for the expanded scope, by mutual agreement.',
            ],
        },
        {
            title: 'Compound',
            window: 'Months 4–6',
            items: [
                'The expanded system runs at full stride and the back catalogue compounds.',
                'Monthly direction reviews keep it sharp.',
            ],
        },
    ],
    timelineNote:
        'The first week is the heavy-lift setup. Once your Content OS is live and the recording rhythm is set, production runs like clockwork.',

    closing: {
        line1: 'Any questions on the agreement or the recording setup, just message me.',
        line2: 'Clear the checklist and we get straight into strategy!',
    },
};

export default function NidhiOnboarding() {
    useEffect(() => {
        document.title = data.pageTitle;
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10 min-h-screen">
            {/* Background Gradient */}
            <div className="fixed inset-0 min-h-screen bg-[#080B11] -z-20 overflow-hidden">
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-indigo-600/20 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-emerald-600/15 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none" />
            </div>

            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-[#07090E]/80 backdrop-blur-lg border-b border-white/5">
                <div className="flex items-center gap-3 w-full max-w-5xl mx-auto">
                    <span className="font-bold tracking-tight text-white text-xl">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/20 mx-2">/</span>
                    <span className="text-white/80 font-medium">{data.brandLabel} Onboarding</span>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 pt-10 space-y-16">

                {/* Intro */}
                <section>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-bold tracking-wide uppercase mb-6">
                        <CheckCircle2 className="w-4 h-4" /> {data.badge}
                    </div>

                    <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight">
                        {data.headline}
                    </h1>

                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 space-y-4 text-white/80 text-lg leading-relaxed shadow-xl">
                        <p><strong>{data.greeting}</strong></p>
                        {data.intro.map((para, i) => <p key={i}>{para}</p>)}
                    </div>
                </section>

                {/* Onboarding checklist */}
                <section>
                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                        <CheckCircle2 className="text-emerald-400 w-6 h-6" /> Your First Steps
                    </h2>

                    <div className="space-y-5">
                        {data.steps
                            .filter((step) => !step.links.some((l) => l.href.includes('_PENDING')))
                            .map((step, i) => {
                                const StepIcon = step.icon;
                                return (
                                    <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-7 shadow-xl hover:border-indigo-500/40 transition-colors">
                                        <div className="flex items-start gap-4">
                                            <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center font-bold relative ${step.done ? 'bg-emerald-500/15 text-emerald-400' : 'bg-indigo-500/15 text-indigo-400'}`}>
                                                <StepIcon className="w-5 h-5" />
                                                <span className={`absolute -top-2 -right-2 w-5 h-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center border-2 border-[#0B0E14] ${step.done ? 'bg-emerald-500' : 'bg-indigo-500'}`}>{step.done ? '✓' : i + 1}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-xl font-bold text-white mb-1 flex items-center gap-2 flex-wrap">{step.title}{step.done && <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">Done</span>}</h3>
                                                <p className="text-slate-400 text-sm leading-relaxed mb-4">{step.desc}</p>

                                                {step.tips && (
                                                    <ul className="mb-4 space-y-2">
                                                        {step.tips.map((tip, t) => (
                                                            <li key={t} className="flex gap-2.5 text-slate-300 text-sm">
                                                                <Zap className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" />
                                                                <span>{tip}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}

                                                <div className="flex flex-wrap gap-3">
                                                    {step.links.map((link, l) => {
                                                        const LinkIcon = link.icon || ExternalLink;
                                                        return (
                                                            <a
                                                                key={l}
                                                                href={link.href}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-500/10 border border-indigo-500/25 text-indigo-300 font-semibold text-sm hover:bg-indigo-500/20 hover:border-indigo-400 hover:text-white transition-colors"
                                                            >
                                                                <LinkIcon className="w-4 h-4" /> {link.label} <MoveRight className="w-4 h-4" />
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </section>

                {/* Timeline */}
                <section>
                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                        <Clock className="text-indigo-400 w-6 h-6" /> {data.timelineTitle}
                    </h2>

                    <div className="relative border-l border-white/10 ml-4 md:ml-6 space-y-10 pb-4">
                        {data.phases.map((phase, i) => (
                            <div key={i} className="relative pl-8 md:pl-10">
                                <div className={`absolute -left-[14px] top-1 w-7 h-7 rounded-full flex items-center justify-center ${
                                    i === 0
                                        ? 'bg-indigo-500 border-[4px] border-[#07090E] shadow-[0_0_15px_rgba(99,102,241,0.5)]'
                                        : 'bg-[#1A1D24] border border-white/20'
                                }`}>
                                    <span className={`text-xs font-bold ${i === 0 ? 'text-white' : 'text-white/60'}`}>{i + 1}</span>
                                </div>
                                <h3 className="text-xl font-bold mb-1 text-white">{phase.title}</h3>
                                <p className={`text-sm font-semibold mb-3 uppercase tracking-wider ${i === 0 ? 'text-indigo-400' : 'text-indigo-400/70'}`}>
                                    {phase.window}
                                </p>
                                <ul className={`space-y-3 ${i === 0 ? 'text-slate-300' : 'text-slate-300/80'}`}>
                                    {phase.items.map((item, j) => (
                                        <li key={j} className="flex gap-3">
                                            <Zap className="w-5 h-5 text-indigo-400 shrink-0" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {data.timelineNote && (
                        <div className="bg-indigo-500/10 border border-indigo-500/20 p-5 rounded-xl text-indigo-200 text-sm leading-relaxed mt-6">
                            <strong className="text-indigo-400">Note:</strong> {data.timelineNote}
                        </div>
                    )}
                </section>

                {/* Closing */}
                <section>
                    <div className="bg-gradient-to-br from-slate-900 to-[#0F172A] border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden text-center">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-[80px] rounded-full pointer-events-none" />
                        <ShieldCheck className="w-9 h-9 text-emerald-400 mx-auto mb-4" />
                        <p className="text-white/60 mb-2">{data.closing.line1}</p>
                        <p className="text-lg font-bold text-white">{data.closing.line2}</p>
                    </div>
                </section>

            </main>
        </div>
    );
}
